import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { PrimaryButton } from '../../components/PrimaryButton';
import { OnboardingHeader } from '../../components/OnboardingHeader';
import { CmsModal } from '../../components/CmsModal';
import { FloatingInput } from '../../components/FloatingInput';
import { LocationAutocomplete } from '../../components/LocationAutocomplete';
import { ThemedDatePicker } from '../../components/ThemedDatePicker';
import {
  MapContainer,
  TileLayer,
  Circle,
  useMap,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

import {
  getMe,
  updateMe,
  uploadFile,
  proposeCustomService,
  saveOnboardingStep,
} from '../../../lib/professional-api';
import {
  listAddons,
  listCategories,
  listServices,
  type ServiceAddonRow,
  type ServiceCategory,
  type ServiceItem,
} from '../../../lib/booking-api';
import { apiErrorMessage } from '../../../lib/api';
import { toast } from '../../../lib/toast';
import { searchNearbyTowns } from '../../../lib/places';

// Hard cap on a pro's travel radius (miles). 500mi was unrealistic given
// the 1-hour buffer the platform allows between appointments; 30mi is the
// most a pro can plausibly cover between back-to-back jobs. Kept in sync
// with the backend validator (ProfessionalProfile.validator) which also
// caps travel_radius_miles at 30.
export const MAX_TRAVEL_RADIUS_MILES = 30;

function MapUpdater({
  center,
  zoom,
}: {
  center: [number, number];
  zoom: number;
}) {
  const map = useMap();
  const [lat, lng] = center;
  // Re-center only when the coordinates or zoom actually change — keying
  // the effect on the array reference re-ran it every render (mapCenter
  // is rebuilt each time), which snapped the map back and made it feel
  // un-draggable. Depending on the primitive lat/lng lets the pro pan.
  useEffect(() => {
    map.setView([lat, lng], zoom);
  }, [lat, lng, zoom, map]);
  return null;
}

interface TimeSlot {
  start: string;
  end: string;
}

interface DayAvailability {
  day: string;
  dow: number; // 0=Sun..6=Sat (matches JS Date#getDay)
  enabled: boolean;
  slots: TimeSlot[];
}

const DAY_DEFS: Array<{ day: string; dow: number }> = [
  { day: 'Monday', dow: 1 },
  { day: 'Tuesday', dow: 2 },
  { day: 'Wednesday', dow: 3 },
  { day: 'Thursday', dow: 4 },
  { day: 'Friday', dow: 5 },
  { day: 'Saturday', dow: 6 },
  { day: 'Sunday', dow: 0 },
];

const TIME_OPTIONS = [
  '8:00',
  '9:00',
  '10:00',
  '11:00',
  '12:00',
  '13:00',
  '14:00',
  '15:00',
  '16:00',
  '17:00',
  '18:00',
  '19:00',
  '20:00',
];

// HH:MM → HH:MM:SS for the backend `time` column.
const toBackendTime = (hhmm: string) => {
  const [h, m] = hhmm.split(':');
  return `${(h || '0').padStart(2, '0')}:${(m || '0').padStart(2, '0')}:00`;
};

// Minutes-since-midnight for slot ordering / overlap math. Zero-padded
// HH:MM compares lexically, but minute math is cleaner.
const toMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10));
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
};

const trimSeconds = (s: string | null | undefined) =>
  s ? s.slice(0, 5) : '';

// Belt-and-suspenders defence: strip AWS presigned query params off an
// S3 URL before sending it back to the backend. The backend's
// validator + service also normalize on save, but stripping here means
// the request body in devtools is clean and we never accidentally
// write a signature into the DB.
const stripS3QueryString = (url: string | null | undefined): string | null => {
  if (!url) return null;
  const qIndex = url.indexOf('?');
  if (qIndex < 0) return url;
  return url.slice(0, qIndex);
};

export default function ServiceOfferPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  // ?step=N persists the wizard position across refresh and the
  // browser back button. Without this a refresh on step 7 (radius)
  // dumped the pro back to step 1 — which they read as "redirected
  // to the previous page". Clamp to 1..10 so a stale link can't
  // crash the renderer with an out-of-range step.
  const [searchParams, setSearchParams] = useSearchParams();
  const parsedStep = Number(searchParams.get('step') ?? '1');
  const initialStep =
    Number.isFinite(parsedStep) && parsedStep >= 1 && parsedStep <= 10
      ? // Step 9 (the old standalone insurance step) was merged into the
        // step-8 documents page — redirect any bookmarked ?step=9 there.
        parsedStep === 9
        ? 8
        : parsedStep
      : 1;
  const [currentSubStep, setCurrentSubStep] = useState(initialStep);
  // Did the pro arrive with an explicit ?step in the URL (a refresh / back /
  // bookmarked link)? If NOT, we resume from the step saved server-side once
  // the profile loads — that's what stops a fresh visit restarting at step 1.
  const hadExplicitStepAtMount = useRef(searchParams.get('step') != null);

  // Mirror the local step back to the URL whenever it changes, using
  // replace: true so each step bump doesn't push a new browser
  // history entry (the Back button still walks one logical step at
  // a time via handleBack).
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (next.get('step') === String(currentSubStep)) return;
    next.set('step', String(currentSubStep));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSubStep]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  // True when the pro is re-entering the wizard after finishing it
  // (Account settings → Edit onboarding). Drives the banner + the
  // bottom-button label so it's clear they're editing existing data.
  const [editMode, setEditMode] = useState(false);
  // True once admin marks the pro VERIFIED — KYC / DBS / insurance docs
  // are then locked in the UI (backend rejects re-upload too).
  const [verifiedLocked, setVerifiedLocked] = useState(false);

  // Catalog data (loaded once on mount).
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [allServices, setAllServices] = useState<ServiceItem[]>([]);
  const [allAddons, setAllAddons] = useState<ServiceAddonRow[]>([]);

  // ---- step-scoped state ----

  // Step 1: which category(s) the pro picked. A pro is signed up to
  // one category at a time today; we keep it an array in case product
  // wants to relax that.
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);

  // Step 2: per-service opt-in within the selected category.
  // Map<serviceId, true> for enabled rows.
  const [enabledServiceIds, setEnabledServiceIds] = useState<Set<string>>(
    new Set(),
  );

  // Step 3: portfolio S3 URLs (the canonical value sent to the backend).
  const [portfolioImages, setPortfolioImages] = useState<string[]>([]);
  // Local blob-URL previews for photos picked in this session. S3 URLs
  // can fail to render in <img> when the bucket is private — the blob
  // URL is what we display, while the S3 URL above is what we submit.
  // Keyed by the S3 URL so we can pair them up on render and revoke
  // the blob when the user removes the photo.
  const [portfolioPreviews, setPortfolioPreviews] = useState<
    Record<string, string>
  >({});
  // Files in-flight to S3 — each carries a blob URL so we can render a
  // skeleton tile with the picked image before the upload returns. We
  // key them by a synthetic id since we don't have an S3 URL yet.
  const [pendingUploads, setPendingUploads] = useState<
    Array<{ id: string; blobUrl: string }>
  >([]);

  // Step 4: about-me / bio.
  const [aboutMe, setAboutMe] = useState('');

  // Step 5: availability per day.
  const [availability, setAvailability] = useState<DayAvailability[]>(
    DAY_DEFS.map((d) => ({
      ...d,
      enabled: false,
      slots: [{ start: '9:00', end: '17:00' }],
    })),
  );

  // Step 6: service-area address. Country is hard-locked to United
  // Kingdom (Cherry Picked is UK-only for launch) and `state` was
  // dropped from the form because we don't store it for now. Lat/lng
  // are required — they come from the Google Places picker, and we
  // refuse to save the step until both are present so the booking
  // radius search can place this pro on a map.
  const [address, setAddress] = useState({
    street: '',
    suite: '',
    city: '',
    zip: '',
    latitude: null as number | null,
    longitude: null as number | null,
  });

  // Step 7: travel radius + towns covered.
  const [radius, setRadius] = useState({ min: 0, max: 7 });
  const [coverageTowns, setCoverageTowns] = useState<string[]>([]);
  const [townInput, setTownInput] = useState('');
  // Catalog of towns/cities discovered via Google Places nearby search.
  // Pre-seeded with whatever the pro had saved (`coverage_towns`) so a
  // returning pro still sees their selection until they Search again.
  const [townCatalog, setTownCatalog] = useState<string[]>([]);
  const [searchingTowns, setSearchingTowns] = useState(false);

  // Step 8: DBS doc. Track the picked File for filename + a blob URL
  // for instant preview (S3 URLs can be private and won't render in
  // <img>); `dbsFileUrl` is the canonical S3 URL we send to the API.
  const [dbsFile, setDbsFile] = useState<File | null>(null);
  const [dbsFileUrl, setDbsFileUrl] = useState<string | null>(null);
  const [dbsPreviewUrl, setDbsPreviewUrl] = useState<string | null>(null);

  // Step 9: insurance doc + meta.
  const [insuranceFile, setInsuranceFile] = useState<File | null>(null);
  const [insuranceFileUrl, setInsuranceFileUrl] = useState<string | null>(null);
  const [insurancePreviewUrl, setInsurancePreviewUrl] = useState<string | null>(
    null,
  );
  const [insuranceProvider, setInsuranceProvider] = useState('');
  const [insurancePolicy, setInsurancePolicy] = useState('');
  const [insuranceExpiry, setInsuranceExpiry] = useState('');

  // Documents step now hosts BOTH the insurance + DBS uploads on one
  // page, sharing the single hidden file input. This tracks which tile
  // the pro clicked so handleFileChange routes the picked file to the
  // right slot (DBS vs insurance). Null = portfolio/other steps.
  const [pendingUploadKind, setPendingUploadKind] = useState<
    'dbs' | 'insurance' | null
  >(null);

  // Step 10: T&C acknowledgement only. Bank account holder / sort code
  // / account number / IBAN used to be collected here and persisted to
  // the `professionals` table. Phase 1-5 of the 2026-06-04 Stripe
  // Connect rollout moved that collection to Stripe's hosted page
  // (migration 1747000000090 dropped the columns), so the wizard's
  // final step is now a Stripe handoff + a single T&C checkbox.
  const [paymentDetails, setPaymentDetails] = useState({
    agreedToTerms: false,
  });
  // Inline per-field error for the T&C checkbox. Populated on Finish
  // and cleared as the user toggles the box.
  const [paymentErrors, setPaymentErrors] = useState<{
    agreedToTerms?: string;
  }>({});
  // T&C modal toggle — opens the CMS-backed terms page in an overlay.
  const [termsOpen, setTermsOpen] = useState(false);

  // Validator for the payment step. Bank fields are entered on
  // Stripe's domain so the only remaining gate here is the T&C
  // acknowledgement.
  const validatePayment = (p: typeof paymentDetails) => {
    const errs: { agreedToTerms?: string } = {};
    if (!p.agreedToTerms) {
      errs.agreedToTerms =
        'You need to agree to the Terms & Conditions to finish.';
    }
    return errs;
  };

  // ---- initial load ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [catRes, svcRes, addonRes, me] = await Promise.all([
          listCategories(),
          listServices(),
          listAddons(),
          getMe(),
        ]);
        if (cancelled) return;

        const cats = catRes.data ?? [];
        const svcs = svcRes.data ?? [];
        const adds = addonRes.data ?? [];
        setCategories(cats);
        setAllAddons(adds);

        const info = me?.info ?? {};

        // Pre-fill from existing pro state.
        const proServices: any[] = Array.isArray(info.services)
          ? info.services
          : [];

        // The public catalogue only returns ACTIVE services, so a
        // custom service this pro proposed (PENDING_APPROVAL) wouldn't
        // appear. Merge the pro's own linked service objects in
        // (dedupe by id) so their pending picks still render with the
        // right category grouping + a "pending" badge.
        const byId = new Map<string, ServiceItem>();
        for (const s of svcs) byId.set(s.id, s);
        for (const row of proServices) {
          const svc = row?.service;
          if (svc?.id && !byId.has(svc.id)) byId.set(svc.id, svc as ServiceItem);
        }
        setAllServices(Array.from(byId.values()));
        if (proServices.length > 0) {
          const catIds = new Set<string>();
          const svcIds = new Set<string>();
          for (const row of proServices) {
            const svc = row?.service;
            if (svc?.category?.id) catIds.add(svc.category.id);
            if (svc?.id) svcIds.add(svc.id);
          }
          setSelectedCategoryIds([...catIds]);
          setEnabledServiceIds(svcIds);
        }

        if (Array.isArray(info.portfolio_images)) {
          // `url` is the canonical S3 URL we keep on the row;
          // `preview_url` is a short-lived signed read URL the backend
          // adds for private-bucket display. Store the canonical one
          // as the state value (sent back on save) and keep the signed
          // one in `portfolioPreviews` so <img> can render it now.
          const urls: string[] = [];
          const previews: Record<string, string> = {};
          for (const p of info.portfolio_images) {
            if (!p?.url) continue;
            urls.push(p.url);
            if (p.preview_url) previews[p.url] = p.preview_url;
          }
          setPortfolioImages(urls);
          if (Object.keys(previews).length > 0) {
            setPortfolioPreviews((prev) => ({ ...prev, ...previews }));
          }
        }

        if (typeof info.bio === 'string') setAboutMe(info.bio);

        const proAvail: any[] = Array.isArray(info.availabilities)
          ? info.availabilities
          : [];
        if (proAvail.length > 0) {
          // Group server rows by dow then project onto the local day
          // shape. Existing days get their first slot's hours; empty
          // days stay in the default shape with enabled=false.
          const byDow = new Map<number, Array<{ start: string; end: string }>>();
          for (const row of proAvail) {
            if (!row?.is_active) continue;
            const list = byDow.get(row.day_of_week) ?? [];
            list.push({
              start: trimSeconds(row.start_time),
              end: trimSeconds(row.end_time),
            });
            byDow.set(row.day_of_week, list);
          }
          setAvailability((prev) =>
            prev.map((d) => {
              const rows = byDow.get(d.dow);
              if (!rows || rows.length === 0) return d;
              return { ...d, enabled: true, slots: rows };
            }),
          );
        }

        setAddress({
          street: info.address_line1 ?? '',
          suite: info.address_line2 ?? '',
          city: info.city ?? '',
          zip: info.postcode ?? '',
          latitude:
            info.latitude !== null && info.latitude !== undefined
              ? Number(info.latitude)
              : null,
          longitude:
            info.longitude !== null && info.longitude !== undefined
              ? Number(info.longitude)
              : null,
        });

        if (typeof info.travel_radius_miles === 'number') {
          // Treat travel_radius_miles as the outer ring; there's no inner-min
          // on the entity, so the inner ring stays at 0. Default range is
          // 0–7 miles when nothing is saved yet. Clamp to the cap so a pro
          // with a pre-cap value (e.g. 500) doesn't silently re-save it.
          setRadius({
            min: 0,
            max: Math.min(info.travel_radius_miles || 7, MAX_TRAVEL_RADIUS_MILES),
          });
        }
        if (Array.isArray(info.coverage_towns)) {
          const towns = info.coverage_towns.filter((t: any) => !!t);
          setCoverageTowns(towns);
          // Seed the chip catalog so the pro sees their saved towns
          // pre-selected before they hit Search again.
          setTownCatalog(towns);
        }

        // Canonical URL goes into state (sent back on save); the
        // server-supplied `*_preview_url` (signed, short-lived) feeds
        // the on-page <img> through `dbsPreviewUrl` / `insurancePreviewUrl`
        // so the saved file is visible even on a private bucket.
        if (info.dbs_document_url) {
          setDbsFileUrl(info.dbs_document_url);
          if (info.dbs_document_preview_url) {
            setDbsPreviewUrl(info.dbs_document_preview_url);
          }
        }
        if (info.insurance_document_url) {
          setInsuranceFileUrl(info.insurance_document_url);
          if (info.insurance_document_preview_url) {
            setInsurancePreviewUrl(info.insurance_document_preview_url);
          }
        }
        if (info.insurance_provider)
          setInsuranceProvider(info.insurance_provider);
        if (info.insurance_policy_number)
          setInsurancePolicy(info.insurance_policy_number);
        if (info.insurance_expiry_date) {
          // Backend returns ISO datetime — we only want YYYY-MM-DD.
          const iso = String(info.insurance_expiry_date).slice(0, 10);
          setInsuranceExpiry(iso);
        }

        // Auto-tick the T&C checkbox on re-entry once the pro has
        // already finished onboarding — they accepted the terms then.
        // The bank_* hydration that used to live here referenced
        // columns dropped by migration 1747000000090; the wizard's
        // step 10 collects no bank info now.
        setPaymentDetails((prev) => ({
          ...prev,
          agreedToTerms: !!info.onboarding_completed_at || prev.agreedToTerms,
        }));

        // Once the pro has finished onboarding we no longer redirect
        // them away from the wizard — they may need to come back and
        // fix something (DBS doc, insurance expiry, coverage towns,
        // travel radius) before the admin approves them. The wizard
        // doubles as the editor for these fields; every step's save
        // path is idempotent so re-running it just updates the row.
        if (info.onboarding_completed_at) setEditMode(true);
        if (info.verification_status === 'VERIFIED') {
          setVerifiedLocked(true);
        }

        // RESUME: a pro returning on a fresh visit (no explicit ?step in the
        // URL) picks up at the furthest step they reached, instead of
        // restarting at step 1. Skip for pros who've already finished
        // onboarding (they're editing — let them land on step 1) and when the
        // URL already pins a step (refresh / back / bookmarked link).
        if (
          !cancelled &&
          !hadExplicitStepAtMount.current &&
          !info.onboarding_completed_at
        ) {
          const saved = Number(info.onboarding_step);
          if (Number.isFinite(saved) && saved >= 1 && saved <= 10) {
            setCurrentSubStep(saved === 9 ? 8 : saved);
          }
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(apiErrorMessage(err, 'Could not load your profile.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  // Catalog slices for the currently-selected category(s).
  const categoryServices = useMemo(() => {
    if (selectedCategoryIds.length === 0) return [];
    const set = new Set(selectedCategoryIds);
    return allServices.filter((s) => set.has((s as any).category?.id ?? ''));
  }, [allServices, selectedCategoryIds]);

  // DBS is mandatory ONLY for childcare professionals — it blocks
  // onboarding until provided. For every other category (cleaning, hair,
  // beauty & wellness) it's an optional "nice to have" that widens the
  // pro's client pool. Driven by whether the pro picked the Childcare
  // category in step 1. (Childcare category code is 'CHILDCARE' — same
  // constant the backend's DBS_REQUIRED_CATEGORY_CODES gate uses.)
  const dbsMandatory = useMemo(
    () =>
      selectedCategoryIds.some(
        (id) =>
          (categories.find((c) => c.id === id)?.code ?? '').toUpperCase() ===
          'CHILDCARE',
      ),
    [selectedCategoryIds, categories],
  );

  // Same set of services, grouped by their parent category so the
  // step-2 list can show "Cleaner" / "Beauty & Wellness" headings —
  // otherwise multi-category pros can't tell which service belongs
  // where.
  const groupedCategoryServices = useMemo(() => {
    if (categoryServices.length === 0) return [];
    const byCategoryId = new Map<
      string,
      { id: string; name: string; sort_order: number; services: ServiceItem[] }
    >();
    for (const svc of categoryServices) {
      const cat = (svc as any).category;
      const id = cat?.id ?? 'other';
      const existing = byCategoryId.get(id);
      if (existing) {
        existing.services.push(svc);
      } else {
        byCategoryId.set(id, {
          id,
          name: cat?.name ?? 'Other',
          sort_order: cat?.sort_order ?? 999,
          services: [svc],
        });
      }
    }
    // Sort categories by their catalog sort_order; sort services
    // within each group by their own sort_order too.
    const groups = Array.from(byCategoryId.values()).sort(
      (a, b) => a.sort_order - b.sort_order,
    );
    for (const g of groups) {
      g.services.sort(
        (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
      );
    }
    return groups;
  }, [categoryServices]);

  // ---- step handlers ----

  const toggleCategory = (categoryId: string) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId],
    );
  };

  const toggleService = (serviceId: string) => {
    setEnabledServiceIds((prev) => {
      const next = new Set(prev);
      if (next.has(serviceId)) next.delete(serviceId);
      else next.add(serviceId);
      return next;
    });
  };

  // Per-category draft text for the "add your own service" inputs, plus
  // which category is mid-submit (to disable its button).
  const [customServiceDraft, setCustomServiceDraft] = useState<
    Record<string, string>
  >({});
  const [proposingCategoryId, setProposingCategoryId] = useState<string | null>(
    null,
  );

  const handleProposeCustomService = async (
    categoryId: string,
    categoryName: string,
  ) => {
    const name = (customServiceDraft[categoryId] ?? '').trim();
    if (name.length < 2) {
      toast.error('Enter a service name (at least 2 characters).');
      return;
    }
    setProposingCategoryId(categoryId);
    try {
      const res = await proposeCustomService({
        service_category_id: categoryId,
        name,
      });
      const created = res.info;
      // Build a ServiceItem-shaped row so it slots into the grouped
      // list, and mark it selected. status PENDING_APPROVAL drives the
      // "pending" badge; it won't be customer-bookable until approved.
      const newRow = {
        id: created.id,
        name: created.name,
        code: created.id,
        description: null,
        image: null,
        pricing_unit: 'PER_BOOKING',
        base_price: 0,
        currency: 'GBP',
        duration_minutes: 60,
        badge: null,
        highlights: null,
        properties: null,
        sort_order: 9999,
        status: created.status,
        category: { id: categoryId, name: categoryName, code: '', sort_order: 999 },
      } as unknown as ServiceItem;
      setAllServices((prev) =>
        prev.some((s) => s.id === newRow.id) ? prev : [...prev, newRow],
      );
      setEnabledServiceIds((prev) => new Set(prev).add(created.id));
      setCustomServiceDraft((prev) => ({ ...prev, [categoryId]: '' }));
      toast.success(res.message || 'Service submitted for approval.');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not add that service.'));
    } finally {
      setProposingCategoryId(null);
    }
  };

  const toggleDay = (day: string) => {
    setAvailability((prev) =>
      prev.map((d) => (d.day === day ? { ...d, enabled: !d.enabled } : d)),
    );
  };

  // Pick a sensible default for a new slot: start where the last slot
  // ends (or 9:00 if there are none), so the pro doesn't get an
  // overlapping duplicate by default.
  const addTimeSlot = (day: string) => {
    setAvailability((prev) =>
      prev.map((d) => {
        if (d.day !== day) return d;
        const lastEnd =
          d.slots.length > 0
            ? toMinutes(d.slots[d.slots.length - 1].end)
            : 9 * 60;
        // Snap to whole hours so we land on a TIME_OPTIONS entry.
        const startMin = Math.min(
          Math.max(lastEnd, 8 * 60),
          19 * 60,
        );
        const endMin = Math.min(startMin + 60, 20 * 60);
        const hh = (m: number) => `${Math.floor(m / 60)}:00`;
        return {
          ...d,
          slots: [...d.slots, { start: hh(startMin), end: hh(endMin) }],
        };
      }),
    );
  };

  const removeTimeSlot = (day: string, slotIndex: number) => {
    setAvailability((prev) =>
      prev.map((d) => {
        if (d.day !== day) return d;
        const next = d.slots.filter((_, i) => i !== slotIndex);
        return {
          ...d,
          enabled: next.length > 0 ? d.enabled : false,
          slots: next.length > 0 ? next : [{ start: '9:00', end: '17:00' }],
        };
      }),
    );
  };

  const updateTime = (
    day: string,
    slotIndex: number,
    type: 'start' | 'end',
    value: string,
  ) => {
    setAvailability((prev) =>
      prev.map((d) =>
        d.day === day
          ? {
              ...d,
              slots: d.slots.map((s, i) =>
                i === slotIndex ? { ...s, [type]: value } : s,
              ),
            }
          : d,
      ),
    );
  };

  // Per-slot validation: each slot must have `start < end` and not
  // overlap any other slot on the same day. Same logic as the
  // dashboard /professional/schedule page so behaviour is identical.
  const slotErrors = useMemo(() => {
    const out: Record<string, Array<string | null>> = {};
    for (const d of availability) {
      const list = d.enabled ? d.slots : [];
      const errs: Array<string | null> = list.map(() => null);
      // Single-slot validity: start < end.
      list.forEach((s, i) => {
        if (s.start && s.end && toMinutes(s.start) >= toMinutes(s.end)) {
          errs[i] = 'End must be after start.';
        }
      });
      // Overlap detection: sort by start, walk and compare to the
      // running max end. The second-onwards slot of any overlapping
      // pair gets flagged so the user knows which row is the duplicate.
      const indexed = list
        .map((s, i) => ({ ...s, i }))
        .sort((a, b) => toMinutes(a.start) - toMinutes(b.start));
      let prevEnd = -1;
      let prevIdx = -1;
      for (const s of indexed) {
        if (errs[s.i]) continue;
        if (toMinutes(s.start) < prevEnd) {
          errs[s.i] = 'Overlaps another slot on the same day.';
          if (prevIdx >= 0 && !errs[prevIdx]) {
            errs[prevIdx] = 'Overlaps another slot on the same day.';
          }
        }
        if (toMinutes(s.end) > prevEnd) {
          prevEnd = toMinutes(s.end);
          prevIdx = s.i;
        }
      }
      out[d.day] = errs;
    }
    return out;
  }, [availability]);

  const hasAnySlotError = useMemo(
    () => Object.values(slotErrors).some((arr) => arr.some(Boolean)),
    [slotErrors],
  );

  const addTown = () => {
    const name = townInput.trim();
    if (!name) return;
    setCoverageTowns((prev) =>
      prev.includes(name) ? prev : [...prev, name],
    );
    // Also add to the catalog so the chip shows up alongside the
    // Google results.
    setTownCatalog((prev) =>
      prev.includes(name) ? prev : [...prev, name],
    );
    setTownInput('');
  };

  const removeTown = (name: string) => {
    setCoverageTowns((prev) => prev.filter((t) => t !== name));
  };

  // Toggle a chip on/off without dropping it from the catalog — the
  // chip stays visible so the pro can re-select later.
  const toggleTown = (name: string) => {
    setCoverageTowns((prev) =>
      prev.includes(name) ? prev.filter((t) => t !== name) : [...prev, name],
    );
  };

  // Ask Google Maps for towns within `radius.max` miles of the pro's
  // address — nearest first. We merge results into `townCatalog`
  // (keeping any free-form additions and previously-saved towns) so the
  // chip grid grows rather than resets.
  const handleSearchTowns = async () => {
    if (searchingTowns) return;
    if (address.latitude == null || address.longitude == null) {
      toast.error(
        'We need your service-area address (with location confirmed) before searching nearby towns.',
      );
      return;
    }
    setSearchingTowns(true);
    try {
      // Google Maps reverse-geocoding of sampled points across the
      // radius — distinct town/locality names, nearest first.
      const collected = await searchNearbyTowns({
        lat: address.latitude!,
        lng: address.longitude!,
        radiusMeters: radius.max * 1609.34,
        limit: 60,
      });

      // Merge: keep saved/typed towns + add anything new from OSM.
      setTownCatalog((prev) => {
        const dedup = new Set(prev.map((t) => t.toLowerCase()));
        const merged = [...prev];
        for (const n of collected) {
          if (!dedup.has(n.toLowerCase())) {
            dedup.add(n.toLowerCase());
            merged.push(n);
          }
        }
        return merged;
      });
      if (collected.length === 0) {
        toast.error(
          'No towns found in this radius — try expanding the slider above.',
        );
      }
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : 'Could not load nearby towns. Try again.',
      );
    } finally {
      setSearchingTowns(false);
    }
  };

  // Auto-load nearby towns when step 7 opens (or when the pro changes
  // the radius). Debounced 900ms so dragging the slider mid-stream
  // doesn't fire an Overpass query on every tick — public mirrors
  // 504 under that kind of load (audit 2026-06-04). The drag must
  // settle for nearly a second before we fire.
  useEffect(() => {
    if (currentSubStep !== 7) return;
    if (address.latitude == null || address.longitude == null) return;
    const t = window.setTimeout(() => {
      handleSearchTowns();
    }, 900);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSubStep, address.latitude, address.longitude, radius.max]);

  const removeImage = (index: number) => {
    const removed = portfolioImages[index];
    const next = portfolioImages.filter((_, i) => i !== index);
    setPortfolioImages(next);
    if (removed) {
      setPortfolioPreviews((prevMap) => {
        if (!prevMap[removed]) return prevMap;
        URL.revokeObjectURL(prevMap[removed]);
        const copy = { ...prevMap };
        delete copy[removed];
        return copy;
      });
    }
    // Auto-persist the trimmed list so a refresh doesn't bring the
    // removed photo back.
    updateMe({
      portfolio_images: next
        .map((u) => stripS3QueryString(u))
        .filter((u): u is string => !!u)
        .map((url) => ({ url, caption: null })),
    }).catch(() => {
      // Best effort — the next Next click will retry the save.
    });
  };

  // Single file picker drives several steps depending on currentSubStep.
  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    // CRITICAL: capture the files into a real array BEFORE resetting
    // `e.target.value`. `e.target.files` is a *live* FileList; clearing
    // the input value empties it, and `files.length === 0` would
    // short-circuit the upload before it ran. This was the silent
    // "nothing happens after I pick a file" bug.
    const filesArr = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';
    if (filesArr.length === 0) return;
    setUploadingFile(true);
    try {
      // Documents step (8) hosts both uploads — route by which tile was
      // clicked (pendingUploadKind), defaulting to insurance.
      if (currentSubStep === 8 && pendingUploadKind === 'dbs') {
        const file = filesArr[0];
        // Show the local preview immediately so the pro can see what
        // they picked even before the S3 round-trip completes.
        const blob = URL.createObjectURL(file);
        setDbsPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return blob;
        });
        setDbsFile(file);
        const res = await uploadFile(file, 'PROFESSIONAL_DBS');
        setDbsFileUrl(res.url);
        // Auto-persist so a refresh doesn't lose the upload. Without
        // this, the URL only hits the DB when the pro clicks Next —
        // any earlier reload would drop the file from their profile.
        await updateMe({ dbs_document_url: stripS3QueryString(res.url) });
      } else if (currentSubStep === 8) {
        const file = filesArr[0];
        const blob = URL.createObjectURL(file);
        setInsurancePreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return blob;
        });
        setInsuranceFile(file);
        const res = await uploadFile(file, 'PROFESSIONAL_INSURANCE');
        setInsuranceFileUrl(res.url);
        await updateMe({
          insurance_document_url: stripS3QueryString(res.url),
        });
      } else {
        // Portfolio step. Push every picked file into `pendingUploads`
        // first so the grid renders a placeholder tile (with the blob
        // preview + spinner) the instant the user picks; each finishing
        // upload then moves its tile from "pending" into the saved
        // portfolio list with a green selected badge.
        const pending = filesArr.map((f) => ({
          id: `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          blobUrl: URL.createObjectURL(f),
        }));
        setPendingUploads((prev) => [...prev, ...pending]);

        let firstError: unknown = null;
        const newlyUploaded: string[] = [];
        for (let i = 0; i < filesArr.length; i += 1) {
          const file = filesArr[i];
          const { id, blobUrl } = pending[i];
          try {
            const res = await uploadFile(file, 'PROFESSIONAL_PORTFOLIO');
            const s3Url = res.url;
            setPortfolioImages((prev) => [...prev, s3Url]);
            setPortfolioPreviews((prev) => ({ ...prev, [s3Url]: blobUrl }));
            setPendingUploads((prev) => prev.filter((p) => p.id !== id));
            newlyUploaded.push(s3Url);
          } catch (err) {
            // Roll back this one tile, surface the error after the loop.
            URL.revokeObjectURL(blobUrl);
            setPendingUploads((prev) => prev.filter((p) => p.id !== id));
            if (firstError == null) firstError = err;
          }
        }
        // Auto-persist the new portfolio list so a refresh keeps the
        // photos. We rebuild from the existing state + the new URLs
        // so we don't depend on a stale read.
        if (newlyUploaded.length > 0) {
          const merged = [...portfolioImages, ...newlyUploaded];
          await updateMe({
            portfolio_images: merged
              .map((u) => stripS3QueryString(u))
              .filter((u): u is string => !!u)
              .map((url) => ({ url, caption: null })),
          });
        }
        if (firstError) throw firstError;
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Upload failed.'));
    } finally {
      setUploadingFile(false);
    }
  };

  // ---- per-step persistence ----

  /**
   * Builds the updateMe patch for the current sub-step and rejects
   * (returns an error string) if the user hasn't entered enough to
   * advance. Returning `null` means the step doesn't persist anything
   * here (e.g. step 1 is just a UI gate for step 2).
   */
  const buildStepPatch = (step: number): Record<string, any> | string | null => {
    switch (step) {
      case 1: {
        if (selectedCategoryIds.length === 0) {
          return 'Pick at least one main service category.';
        }
        return null;
      }
      case 2: {
        if (enabledServiceIds.size === 0) {
          return 'Turn on at least one service before continuing.';
        }
        const services = [...enabledServiceIds].map((service_id) => ({
          service_id,
          custom_price: null,
          custom_duration_minutes: null,
          is_active: true,
        }));
        return { services };
      }
      case 3: {
        // Portfolio is OPTIONAL — some categories (childcare in particular)
        // have no work photos to show, so an empty gallery must not block
        // onboarding. Persist whatever's there (possibly nothing) and continue;
        // the step also offers an explicit "Skip for now".
        return {
          portfolio_images: portfolioImages
            .map((u) => stripS3QueryString(u))
            .filter((u): u is string => !!u)
            .map((url) => ({ url, caption: null })),
        };
      }
      case 4: {
        if (!aboutMe.trim()) {
          return 'Tell clients a bit about yourself before continuing.';
        }
        return { bio: aboutMe.trim() };
      }
      case 5: {
        if (hasAnySlotError) {
          return 'Fix the highlighted overlapping or invalid time slots before continuing.';
        }
        const availabilities: Array<{
          day_of_week: number;
          start_time: string;
          end_time: string;
          is_active: boolean;
        }> = [];
        for (const d of availability) {
          if (!d.enabled) continue;
          for (const slot of d.slots) {
            availabilities.push({
              day_of_week: d.dow,
              start_time: toBackendTime(slot.start),
              end_time: toBackendTime(slot.end),
              is_active: true,
            });
          }
        }
        if (availabilities.length === 0) {
          return 'Add at least one available day with a time slot.';
        }
        return { availabilities };
      }
      case 6: {
        if (!address.street.trim()) {
          return 'Pick your street address from the Google suggestions.';
        }
        if (!address.city.trim()) {
          return 'City could not be detected — pick a more specific address.';
        }
        if (!address.zip.trim()) {
          return 'Postcode could not be detected — pick a more specific address.';
        }
        // Lat/lng are mandatory: the booking radius search uses them.
        if (address.latitude == null || address.longitude == null) {
          return 'Pick the address from the Google suggestions so we can save its location.';
        }
        return {
          address_line1: address.street.trim(),
          address_line2: address.suite.trim() || null,
          city: address.city.trim(),
          state: null,
          postcode: address.zip.trim(),
          country_code: 'GB',
          latitude: address.latitude,
          longitude: address.longitude,
        };
      }
      case 7: {
        if (radius.max <= 0) {
          return 'Set a travel radius greater than zero.';
        }
        return {
          travel_radius_miles: radius.max,
          coverage_towns: coverageTowns,
        };
      }
      case 8: {
        // Consolidated documents page. Public liability insurance is
        // required for everyone; DBS is required only for childcare pros
        // (optional, but encouraged, for all other categories).
        if (!insuranceFileUrl) {
          return 'Upload your public liability insurance document to continue.';
        }
        if (!insuranceExpiry) {
          return 'Pick the expiry date on your insurance certificate.';
        }
        if (dbsMandatory && !dbsFileUrl) {
          return 'Childcare professionals must upload an enhanced DBS check to continue.';
        }
        return {
          insurance_document_url: insuranceFileUrl,
          insurance_provider: insuranceProvider.trim() || null,
          insurance_policy_number: insurancePolicy.trim() || null,
          insurance_expiry_date: insuranceExpiry,
          // Optional for non-childcare — only send when actually provided.
          ...(dbsFileUrl ? { dbs_document_url: dbsFileUrl } : {}),
        };
      }
      case 10: {
        const errs = validatePayment(paymentDetails);
        setPaymentErrors(errs);
        const firstError = Object.values(errs)[0];
        if (firstError) {
          // Inline errors are now shown under each field; the toast
          // echoes the first one so the user isn't left guessing if
          // the offending field is scrolled off-screen.
          return firstError;
        }
        // Bank fields are no longer collected on this step — the
        // pro is redirected to Stripe Connect's hosted onboarding
        // from the parent funnel after we stamp
        // onboarding_completed_at. The backend strips any legacy
        // bank_* fields via stripUnknown so older clients still
        // work.
        return {
          onboarding_completed_at: new Date().toISOString(),
        };
      }
      default:
        return null;
    }
  };

  const handleNext = async () => {
    if (saving || uploadingFile) return;
    const patch = buildStepPatch(currentSubStep);
    if (typeof patch === 'string') {
      // Validation messages go to toast — no inline banner needed.
      toast.error(patch);
      return;
    }
    if (patch && Object.keys(patch).length > 0) {
      setSaving(true);
      try {
        await updateMe(patch);
      } catch (err) {
        toast.error(apiErrorMessage(err, 'Could not save this step.'));
        setSaving(false);
        return;
      } finally {
        setSaving(false);
      }
    }
    // Step 10 is now the "we'll hand you to Stripe" handoff. The
    // wizard itself is done — onboarding_completed_at was already
    // stamped on save above — and we redirect the pro to
    // /professional/setup-payouts so Stripe Connect's hosted page
    // can collect bank details on Stripe's domain. (Edit-mode
    // re-entry skips Stripe and returns to settings; they may
    // already have a Stripe account set up.)
    if (currentSubStep === 10) {
      // Fire-and-forget: mark the wizard fully reached. Never block the
      // completion navigation on this progress-marker save.
      void saveOnboardingStep(10).catch(() => {});
      if (editMode) {
        toast.success('Changes saved.');
        navigate('/professional/account-settings');
        return;
      }
      toast.success('Onboarding complete! Now let\'s set up your payouts.');
      navigate('/professional/setup-payouts');
      return;
    }
    // Step 8 is now the consolidated documents page (insurance + DBS);
    // the old separate insurance step (9) is gone, so jump straight to
    // payments (10).
    if (currentSubStep === 8) {
      void saveOnboardingStep(10).catch(() => {});
      setCurrentSubStep(10);
      return;
    }
    // Persist the position we're advancing TO so a returning pro resumes
    // there. Fire-and-forget — the step's data was already saved above.
    void saveOnboardingStep(currentSubStep + 1).catch(() => {});
    setCurrentSubStep((s) => s + 1);
  };

  const handleBack = () => {
    // Mirror the forward skip: payments (10) steps back to the merged
    // documents page (8), not the retired insurance step (9).
    if (currentSubStep === 10) {
      setCurrentSubStep(8);
      return;
    }
    if (currentSubStep > 1 && currentSubStep <= 10) {
      setCurrentSubStep((s) => s - 1);
    } else {
      navigate(-1);
    }
  };

  const triggerFileUpload = (kind?: 'dbs' | 'insurance') => {
    setPendingUploadKind(kind ?? null);
    fileInputRef.current?.click();
  };

  const steps = [
    { id: 1, label: 'Set up profile', active: currentSubStep >= 1 },
    { id: 2, label: 'Location', active: currentSubStep >= 6 },
    { id: 3, label: 'Certifications', active: currentSubStep >= 8 },
    { id: 4, label: 'Payment', active: currentSubStep >= 10 },
  ];

  // ---- Step 11: success ----
  if (currentSubStep === 11) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center font-['DM_Sans',sans-serif] px-4">
        <div className="flex flex-col items-center max-w-[600px] text-center">
          <div className="relative mb-8">
            <svg
              width="181"
              height="125"
              viewBox="0 0 181 125"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_1_16976)">
                <path
                  d="M174.613 124C185.347 97.1844 182.1 67.2418 168.256 44.2452L23.3348 64.2955L162.215 35.3746C103.683 -37.5584 -10.2415 10.8172 0.740232 102.9L174.613 124Z"
                  fill="white"
                />
                <path
                  d="M174.613 124C185.347 97.1844 182.1 67.2418 168.256 44.2452L23.3348 64.2955L162.215 35.3746C103.683 -37.5584 -10.2415 10.8172 0.740232 102.9L174.613 124Z"
                  fill="#3A3335"
                  fillOpacity="0.04"
                />
                <circle cx="89.5" cy="88.5" r="36.5" fill="#C6D8D3" />
                <path
                  d="M86.5 93.9219L98.8594 81.5078L100.5 83.1484L86.5 97.1484L79.9922 90.6406L81.5781 89L86.5 93.9219Z"
                  fill="white"
                />
              </g>
              <defs>
                <clipPath id="clip0_1_16976">
                  <rect width="181" height="125" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </div>

          <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[34px] leading-tight mb-4">
            You're all set!
          </h1>
          <p className="text-[#908e8e] text-[15px] leading-relaxed mb-12">
            Once your profile is reviewed, you'll be ready to start
            <br className="hidden sm:block" /> accepting bookings and earning.
          </p>

          <div className="w-full max-w-[420px]">
            <PrimaryButton
              onClick={() => navigate('/professional/dashboard')}
              className="w-full py-4"
            >
              Go to dashboard
            </PrimaryButton>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center text-[#908e8e]">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col font-['DM_Sans',sans-serif]">
      <OnboardingHeader />

      <main className="flex-1 flex flex-col items-center py-6 md:py-12 px-4 w-full max-w-[1140px] mx-auto">
        {/* Title */}
        <div className="text-center mb-12">
          <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[25px] md:text-[34px] leading-tight mb-2">
            {currentSubStep === 1
              ? 'What service do you offer?'
              : currentSubStep === 2
              ? 'Set up your profile'
              : currentSubStep === 3
              ? 'Add Portfolio'
              : currentSubStep === 4
              ? 'Tell us about yourself'
              : currentSubStep === 5
              ? 'Setup your availability'
              : currentSubStep === 6
              ? 'Service Area'
              : currentSubStep === 7
              ? 'Set your travel radius'
              : currentSubStep === 8
              ? 'Documents'
              : 'Set up payments'}
          </h1>
          <p className="text-[#908e8e] text-[14px]">
            {currentSubStep === 1
              ? ''
              : currentSubStep === 2
              ? 'Show clients what you offer'
              : currentSubStep === 3
              ? 'Show clients your previous work'
              : currentSubStep === 4
              ? "Tell clients a bit about yourself, your experience, and your specialties."
              : currentSubStep === 5
              ? 'You can change it later in your schedule settings'
              : currentSubStep === 6
              ? 'Where do you work?'
              : currentSubStep === 7
              ? "Choose how far you're happy to travel, then select the towns you cover."
              : currentSubStep === 8
              ? 'Upload your documents — required ones are marked, the rest are optional'
              : "Last step — we'll hand you off to Stripe to collect your bank details securely."}
          </p>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-center mb-10 w-full max-w-[700px]">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className={`w-[24px] h-[24px] rounded-full flex items-center justify-center mb-2 ${step.active ? 'bg-[#8e2a2f]' : 'bg-[#edf2f0]'}`}
                >
                  <div
                    className={`rounded-full ${step.active ? 'w-[8px] h-[8px] bg-white' : 'w-[6px] h-[6px] bg-[#c6d8d3]'}`}
                  />
                </div>
                <p className="text-[#908e8e] text-[12px] md:text-sm leading-tight whitespace-nowrap font-bold">
                  {step.label}
                </p>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`w-8 sm:w-16 md:w-24 h-[2px] mb-[24px] mx-1 shrink-0 ${steps[index + 1].active ? 'bg-[#8e2a2f]' : 'bg-[#edf2f0]'}`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Validation + save errors surface as toasts now — no inline
            banner here. */}

        {/* Edit mode banner — pros re-entering after onboarding to fix
            wrong details see a clear note that they're editing and can
            navigate steps. Hidden during the first run through. */}
        {editMode ? (
          <div className="mb-6 w-full max-w-[614px] rounded-[10px] bg-[#f9f7f2] border border-[#eee] px-4 py-3 flex items-start gap-3">
            <span className="material-icons text-[#8e2a2f] text-[18px] mt-0.5">
              edit
            </span>
            <div className="flex-1 text-[13px]">
              <p className="font-bold text-[#3a3335]">
                Editing your profile
              </p>
              <p className="text-[#908e8e] mt-0.5">
                Use Next / Back to walk through each step. Changes are
                saved as you go. Click Finish on the last step to head
                back to your account settings.
              </p>
            </div>
          </div>
        ) : null}

        {/* Form */}
        <div
          className={`w-full flex flex-col items-center gap-6 ${currentSubStep === 7 ? 'max-w-[630px]' : 'max-w-[614px]'}`}
        >
          {currentSubStep <= 2 && (
            <p className="font-bold text-[#908e8e] text-[14px] w-full text-left mb-2">
              {currentSubStep === 1
                ? 'Please select your main service category'
                : 'Please select your main services'}
            </p>
          )}

          {/* Step 1: categories */}
          {currentSubStep === 1 && (
            <div className="w-full space-y-4">
              {categories.length === 0 ? (
                <p className="text-sm text-[#908e8e]">No categories available.</p>
              ) : (
                categories.map((category) => {
                  const isSelected = selectedCategoryIds.includes(category.id);
                  return (
                    <label
                      key={category.id}
                      className="flex items-center gap-4 cursor-pointer group"
                    >
                      <div className="relative flex items-center justify-center">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={isSelected}
                          onChange={() => toggleCategory(category.id)}
                        />
                        <div
                          className={`w-5 h-5 rounded border-2 transition-all flex items-center justify-center ${isSelected ? 'bg-[#8e2a2f] border-[#8e2a2f]' : 'bg-white border-[#eee] group-hover:border-[#8e2a2f]/50'}`}
                        >
                          {isSelected && (
                            <span className="material-icons text-white text-[14px]">
                              check
                            </span>
                          )}
                        </div>
                      </div>
                      <span
                        className={`font-bold text-[16px] transition-colors ${isSelected ? 'text-[#3a3335]' : 'text-[#908e8e]'}`}
                      >
                        {category.name}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
          )}

          {/* Step 2: services grouped by category so multi-category pros
              can tell which service belongs where. */}
          {currentSubStep === 2 && (
            <div className="w-full space-y-10 mb-16">
              {groupedCategoryServices.length === 0 ? (
                <p className="text-sm text-[#908e8e]">
                  No services configured for your selected category yet.
                </p>
              ) : (
                groupedCategoryServices.map((group) => {
                  const selectedInGroup = group.services.filter((s) =>
                    enabledServiceIds.has(s.id),
                  ).length;
                  return (
                    <div key={group.id} className="space-y-5">
                      {/* Category heading — burgundy band matches the
                          dashboard /services page so the visual language
                          is consistent. */}
                      <div className="flex items-center justify-between bg-[#f2eee2] px-4 py-2 rounded-[6px]">
                        <h3 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[15px]">
                          {group.name}
                        </h3>
                        <span className="text-[12px] font-bold text-[#8e2a2f]">
                          {selectedInGroup} / {group.services.length}
                        </span>
                      </div>
                      <div className="space-y-6 px-2">
                        {group.services.map((service) => {
                          const on = enabledServiceIds.has(service.id);
                          const pending =
                            (service as any).status === 'PENDING_APPROVAL';
                          return (
                            <div
                              key={service.id}
                              className="flex items-center justify-between"
                            >
                              <span className="font-bold text-[#3a3335] text-[16px]">
                                {service.name}
                                {pending ? (
                                  <span className="ml-2 align-middle inline-block rounded-full bg-[#f2eee2] px-2 py-0.5 text-[11px] font-bold text-[#8e2a2f]">
                                    Pending approval
                                  </span>
                                ) : null}
                              </span>
                              <button
                                type="button"
                                onClick={() => toggleService(service.id)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${on ? 'bg-[#8e2a2f]' : 'bg-[#908e8e]'}`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`}
                                />
                              </button>
                            </div>
                          );
                        })}

                        {/* Add-your-own: propose a service that isn't
                            listed. It's saved as pending and reviewed by
                            our team before customers can book it. */}
                        <div className="pt-2">
                          <p className="text-[12px] text-[#908e8e] mb-2">
                            Can't find a service you offer? Add it below — we'll
                            review it before it goes live.
                          </p>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={customServiceDraft[group.id] ?? ''}
                              onChange={(e) =>
                                setCustomServiceDraft((prev) => ({
                                  ...prev,
                                  [group.id]: e.target.value,
                                }))
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleProposeCustomService(
                                    group.id,
                                    group.name,
                                  );
                                }
                              }}
                              placeholder={`e.g. Sports Massage`}
                              maxLength={120}
                              className="flex-1 h-[44px] px-4 rounded-[10px] border border-[#ede8e9] font-['DM_Sans',sans-serif] text-[14px] focus:outline-none focus:border-[#8e2a2f] transition-all placeholder:text-[#908e8e]"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                handleProposeCustomService(group.id, group.name)
                              }
                              disabled={proposingCategoryId === group.id}
                              className="px-5 h-[44px] bg-[#8e2a2f] hover:bg-[#7a2428] text-white rounded-[10px] font-['DM_Sans',sans-serif] font-bold text-[13px] transition-all uppercase tracking-wide disabled:bg-[#cfcccd]"
                            >
                              {proposingCategoryId === group.id ? 'Adding…' : 'Add'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              {allAddons.length > 0 ? (
                <p className="text-[12px] text-[#908e8e] text-center mt-6">
                  You can opt into add-ons later from{' '}
                  <span className="font-bold">Services</span> in your dashboard.
                </p>
              ) : null}
            </div>
          )}

          {/* Step 3: portfolio — gallery grid with always-visible × on
              each tile, and a single "Upload photos" button under the
              grid that doubles as the "add more" trigger. Matches the
              Figma. */}
          {currentSubStep === 3 && (
            <div className="w-full flex flex-col items-center">
              <div className="w-full border-2 border-dashed border-[#eee] rounded-2xl p-6 md:p-8">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  multiple
                  accept="image/*"
                  className="hidden"
                />

                {portfolioImages.length === 0 && pendingUploads.length === 0 ? (
                  <p className="text-center text-sm text-[#908e8e] mb-6">
                    No photos yet — upload a few to show your work.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-6">
                    {portfolioImages.map((img, index) => {
                      // Prefer the in-session blob URL — robust to a
                      // private S3 bucket; fall back to the S3 URL
                      // otherwise (e.g. images loaded from getMe on
                      // mount).
                      const src = portfolioPreviews[img] ?? img;
                      return (
                        <div
                          key={`${img}-${index}`}
                          className="relative aspect-square rounded-lg overflow-hidden bg-[#f7f7f7]"
                        >
                          <img
                            src={src}
                            alt={`Portfolio ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-2 right-2 w-7 h-7 bg-white rounded-md flex items-center justify-center shadow-md hover:bg-[#fbeaea]"
                            title="Remove"
                            aria-label={`Remove photo ${index + 1}`}
                          >
                            <span className="material-icons text-[16px] text-[#3a3335]">
                              close
                            </span>
                          </button>
                        </div>
                      );
                    })}

                    {/* Pending tiles — show the picked file right away
                        with a soft overlay until S3 returns. */}
                    {pendingUploads.map((p) => (
                      <div
                        key={p.id}
                        className="relative aspect-square rounded-lg overflow-hidden bg-[#f7f7f7]"
                      >
                        <img
                          src={p.blobUrl}
                          alt="Uploading"
                          className="w-full h-full object-cover opacity-60"
                        />
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-white bg-black/30">
                          <span className="material-icons animate-spin text-[24px]">
                            progress_activity
                          </span>
                          <span className="text-[10px] font-bold mt-1 uppercase tracking-wide">
                            Uploading…
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex flex-col items-center gap-3">
                  <PrimaryButton
                    onClick={triggerFileUpload}
                    disabled={uploadingFile}
                    className="!bg-[#7b8778] hover:!bg-[#6a7467] w-auto disabled:opacity-60"
                  >
                    <div className="flex items-center gap-3">
                      <span className="material-icons text-xl leading-none">
                        cloud_upload
                      </span>
                      <span>
                        {uploadingFile ? 'Uploading…' : 'Upload photos'}
                      </span>
                    </div>
                  </PrimaryButton>

                  {/* Portfolio is optional — some categories (e.g. childcare)
                      have no photos to show. Let the pro continue without any.
                      Only offered while the gallery is empty; once they've
                      added photos the normal "Next" is the obvious action. */}
                  {portfolioImages.length === 0 &&
                  pendingUploads.length === 0 ? (
                    <button
                      type="button"
                      onClick={() => void handleNext()}
                      disabled={saving || uploadingFile}
                      className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px] font-bold underline underline-offset-4 hover:text-[#8e2a2f] transition-colors disabled:opacity-60"
                    >
                      Skip for now
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: about me */}
          {currentSubStep === 4 && (
            <div className="w-full flex flex-col items-start">
              <p className="font-bold text-[#908e8e] text-[14px] mb-2">
                About me
              </p>
              <div
                className="w-full p-6 border border-[#eee] rounded-xl bg-white min-h-[200px]"
                style={{
                  boxShadow: '0px 20px 24px rgba(153, 155, 168, 0.15)',
                }}
              >
                <textarea
                  className="w-full h-full min-h-[150px] resize-none outline-none text-[#3a3335] text-[15px] leading-relaxed"
                  value={aboutMe}
                  onChange={(e) => setAboutMe(e.target.value)}
                  placeholder="Tell clients about yourself..."
                />
              </div>
            </div>
          )}

          {/* Step 5: availability */}
          {currentSubStep === 5 && (
            <div className="w-full flex flex-col items-center">
              <div
                className="w-full p-8 border border-[#eee] rounded-2xl bg-white"
                style={{
                  boxShadow: '0px 20px 24px rgba(153, 155, 168, 0.15)',
                }}
              >
                <p className="font-bold text-[#3a3335] text-[14px] mb-6">
                  Available days
                </p>
                <div className="space-y-6">
                  {availability.map((day) => (
                    <div
                      key={day.day}
                      className="flex flex-col md:flex-row items-start gap-4 md:gap-8 pb-6 md:pb-0 border-b border-[#eee] md:border-0 last:border-0"
                    >
                      <div className="flex items-center gap-3 w-full md:w-[120px] shrink-0 pt-0 md:pt-2">
                        <input
                          type="checkbox"
                          checked={day.enabled}
                          onChange={() => toggleDay(day.day)}
                          className="w-4 h-4 rounded border-[#eee] text-[#8e2a2f] focus:ring-[#8e2a2f] accent-[#8e2a2f] cursor-pointer"
                        />
                        <span
                          className={`font-bold text-[14px] ${day.enabled ? 'text-[#3a3335]' : 'text-[#908e8e]'}`}
                        >
                          {day.day}
                        </span>
                      </div>
                      <div className="w-full flex-1">
                        {day.enabled ? (
                          <div className="space-y-3">
                            {day.slots.map((slot, index) => {
                              const err = slotErrors[day.day]?.[index] ?? null;
                              const borderClass = err
                                ? 'border-red-400'
                                : 'border-[#eee]';
                              return (
                                <div key={index}>
                                  <div className="flex items-center gap-3">
                                    <div className="relative flex-1">
                                      <select
                                        value={slot.start}
                                        onChange={(e) =>
                                          updateTime(
                                            day.day,
                                            index,
                                            'start',
                                            e.target.value,
                                          )
                                        }
                                        className={`w-full h-11 px-4 border rounded-lg text-[14px] outline-none appearance-none bg-white pr-10 ${borderClass}`}
                                      >
                                        {TIME_OPTIONS.map((t) => (
                                          <option key={t} value={t}>
                                            {t}
                                          </option>
                                        ))}
                                      </select>
                                      <span className="material-icons absolute right-3 top-1/2 -translate-y-1/2 text-[#908e8e] pointer-events-none text-xl">
                                        keyboard_arrow_down
                                      </span>
                                    </div>
                                    <div className="relative flex-1">
                                      <select
                                        value={slot.end}
                                        onChange={(e) =>
                                          updateTime(
                                            day.day,
                                            index,
                                            'end',
                                            e.target.value,
                                          )
                                        }
                                        className={`w-full h-11 px-4 border rounded-lg text-[14px] outline-none appearance-none bg-white pr-10 ${borderClass}`}
                                      >
                                        {TIME_OPTIONS.map((t) => (
                                          <option key={t} value={t}>
                                            {t}
                                          </option>
                                        ))}
                                      </select>
                                      <span className="material-icons absolute right-3 top-1/2 -translate-y-1/2 text-[#908e8e] pointer-events-none text-xl">
                                        keyboard_arrow_down
                                      </span>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeTimeSlot(day.day, index)
                                      }
                                      className="w-10 h-11 flex items-center justify-center text-[#908e8e] hover:text-[#8e2a2f] transition-colors bg-[#f9f9f9] rounded-lg shrink-0"
                                    >
                                      <span className="material-icons text-[20px]">
                                        delete
                                      </span>
                                    </button>
                                  </div>
                                  {err ? (
                                    <p className="mt-1 text-[12px] text-red-600 font-bold">
                                      {err}
                                    </p>
                                  ) : null}
                                </div>
                              );
                            })}
                            <button
                              type="button"
                              onClick={() => addTimeSlot(day.day)}
                              className="text-[#8e2a2f] font-bold text-[13px] underline inline-block"
                            >
                              Add another
                            </button>
                          </div>
                        ) : (
                          <span className="text-[#908e8e] text-[14px] pt-0 md:pt-2 inline-block">
                            Unavailable
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 6: address — locked to UK, picker drives lat/lng,
              city, postcode. Only line 2 is freely editable. */}
          {currentSubStep === 6 && (
            <div className="w-full flex flex-col items-center gap-2">
              {/* Country: hard-locked to the UK for launch. Rendered
                  as a read-only field so the pro can see it but not
                  change it — FloatingInput has no disabled mode so we
                  use a static styled div. */}
              <div className="h-[70px] relative w-full bg-[#f9f9f9] border border-[#eee] rounded-[8px] cursor-not-allowed">
                <span className="absolute left-[20px] top-[12px] font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px]">
                  Country
                </span>
                <span className="absolute left-[20px] top-[34px] font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px]">
                  United Kingdom
                </span>
                <span className="material-icons absolute right-[20px] top-1/2 -translate-y-1/2 text-[#908e8e] !text-[18px]">
                  lock
                </span>
              </div>

              <LocationAutocomplete
                label="Street address"
                required
                value={address.street}
                onChange={(val) =>
                  // Free typing voids any previously-picked lat/lng —
                  // re-pick from the suggestions to re-validate.
                  setAddress((prev) => ({
                    ...prev,
                    street: val,
                    latitude: null,
                    longitude: null,
                  }))
                }
                onPlaceSelected={(place) => {
                  setAddress((prev) => ({
                    ...prev,
                    street: place.description,
                    city: place.city ?? prev.city,
                    zip: place.postcode ?? prev.zip,
                    latitude: place.latitude ?? null,
                    longitude: place.longitude ?? null,
                  }));
                }}
                placeholder="Start typing your address…"
              />
              <p className="text-[12px] text-[#908e8e] -mt-1 self-start">
                Pick from the Google suggestions so we can save the exact
                location.
              </p>

              <FloatingInput
                label="Suite, apartment number (optional)"
                value={address.suite}
                onChange={(val) => setAddress({ ...address, suite: val })}
              />

              {/* City + Postcode mirror what the picker resolved. They
                  stay editable in case Google's split was off, but they
                  start populated. */}
              <div className="w-full flex gap-2">
                <FloatingInput
                  label="City"
                  required
                  value={address.city}
                  onChange={(val) => setAddress({ ...address, city: val })}
                />
                <FloatingInput
                  label="Postcode"
                  required
                  value={address.zip}
                  onChange={(val) => setAddress({ ...address, zip: val })}
                />
              </div>

              {address.latitude != null && address.longitude != null ? (
                <p className="text-[12px] text-[#2e7d32] font-bold self-start mt-1">
                  Address location confirmed.
                </p>
              ) : (
                <p className="text-[12px] text-[#908e8e] self-start mt-1">
                  Latitude/longitude required — pick the address from the
                  dropdown to lock it in.
                </p>
              )}
            </div>
          )}

          {/* Step 7: travel radius + towns — map centered on the pro's
              service-area address (from step 6) and the Search button
              pulls nearby cities from Google Places. */}
          {currentSubStep === 7 && (
            <div className="w-full flex flex-col items-center">
              {(() => {
                // Fall back to a UK midpoint when lat/lng is missing —
                // shouldn't happen because step 6 enforces it, but the
                // map shouldn't crash if a returning pro is mid-edit.
                const mapCenter: [number, number] =
                  address.latitude != null && address.longitude != null
                    ? [address.latitude, address.longitude]
                    : [52.5695, -0.2405];
                const mapZoom =
                  radius.max > 250
                    ? 5
                    : radius.max > 120
                      ? 6
                      : radius.max > 60
                        ? 7
                        : radius.max > 30
                          ? 8
                          : radius.max > 15
                            ? 9
                            : 10;
                return (
                  <div
                    className="w-full rounded-2xl overflow-hidden mb-8 border border-[#eee]"
                    style={{
                      height: '300px',
                      boxShadow:
                        '0px 20px 24px rgba(153, 155, 168, 0.15)',
                    }}
                  >
                    <MapContainer
                      center={mapCenter}
                      zoom={mapZoom}
                      scrollWheelZoom={true}
                      style={{ height: '100%', width: '100%' }}
                      attributionControl={true}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <Circle
                        center={mapCenter}
                        radius={radius.max * 1609.34}
                        interactive={false}
                        pathOptions={{
                          color: '#8e2a2f',
                          fillColor: '#8e2a2f',
                          fillOpacity: 0.12,
                          weight: 2,
                        }}
                      />
                      <Circle
                        center={mapCenter}
                        radius={radius.min * 1609.34}
                        interactive={false}
                        pathOptions={{
                          color: '#8e2a2f',
                          fillColor: '#8e2a2f',
                          fillOpacity: 0.08,
                          weight: 1,
                          dashArray: '5, 8',
                        }}
                      />
                      <MapUpdater center={mapCenter} zoom={mapZoom} />
                    </MapContainer>
                  </div>
                );
              })()}

              <div
                className="w-full p-4 md:p-8 border border-[#eee] rounded-2xl bg-white mb-8"
                style={{
                  boxShadow: '0px 20px 24px rgba(153, 155, 168, 0.15)',
                }}
              >
                <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-8 mb-2">
                  <div className="flex flex-row md:flex-col items-center gap-2 md:gap-0">
                    <span className="text-[#908e8e] text-[12px] font-bold mb-0 md:mb-1">
                      From
                    </span>
                    <div className="w-[80px] md:w-[100px] h-12 md:h-14 border border-[#eee] rounded-lg flex items-center justify-center text-[#3a3335] font-bold text-[14px] md:text-[15px]">
                      {radius.min} mi
                    </div>
                  </div>

                  <div className="flex-1 w-full flex flex-col items-center relative">
                    <span className="text-[#3a3335] font-bold text-[14px] mb-8">
                      Radius of travel{' '}
                      <span className="text-[#908e8e] font-normal ml-4">
                        miles
                      </span>
                    </span>

                    {/* Single-thumb slider — controls the OUTER ring
                        only. travel_radius_miles on the entity has no
                        inner-min counterpart, so the previous dual-
                        range UI just stacked two <input type=range>
                        elements over each other and the higher-z one
                        blocked drags on the lower one (audit 2026-06-
                        04: "Radius of Travel" slider non-functional
                        on production). Single input, native browser
                        interaction, no pointer-events hacks. */}
                    <div className="relative w-full h-1.5 bg-[#edf2f0] rounded-full mt-2">
                      <div
                        className="absolute bg-[#8e2a2f] h-full rounded-full pointer-events-none"
                        style={{
                          left: '0%',
                          right: `${100 - (radius.max / MAX_TRAVEL_RADIUS_MILES) * 100}%`,
                        }}
                      />

                      <input
                        type="range"
                        min="0"
                        max={MAX_TRAVEL_RADIUS_MILES}
                        value={radius.max}
                        onChange={(e) => {
                          const val = Math.min(
                            MAX_TRAVEL_RADIUS_MILES,
                            Math.max(0, Number(e.target.value)),
                          );
                          setRadius({ ...radius, max: val });
                        }}
                        aria-label="Maximum travel radius in miles"
                        className="absolute w-full -top-1.5 h-4 opacity-0 cursor-pointer z-20"
                      />

                      <div
                        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-7 h-7 bg-[#8e2a2f] rounded-full shadow-lg z-30 pointer-events-none flex items-center justify-center border-2 border-white"
                        style={{ left: `${(radius.max / MAX_TRAVEL_RADIUS_MILES) * 100}%` }}
                      >
                        <div className="w-2 h-2 bg-white rounded-full" />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-row md:flex-col items-center gap-2 md:gap-0">
                    <span className="text-[#908e8e] text-[12px] font-bold mb-0 md:mb-1">
                      To
                    </span>
                    <div className="w-[80px] md:w-[100px] h-12 md:h-14 border border-[#eee] rounded-lg flex items-center justify-center text-[#3a3335] font-bold text-[14px] md:text-[15px]">
                      {radius.max} mi
                    </div>
                  </div>
                </div>

                {/* Search button — queries Google Places for towns
                    within the chosen radius around the pro's address. */}
                <div className="flex justify-center mt-6">
                  <button
                    type="button"
                    onClick={handleSearchTowns}
                    disabled={searchingTowns}
                    className="px-12 py-3 bg-[#f2eee6] text-[#3a3335] font-bold rounded-lg text-sm hover:opacity-90 transition-all disabled:opacity-60"
                  >
                    {searchingTowns ? 'Searching…' : 'Search'}
                  </button>
                </div>
              </div>

              <div className="w-full mb-4">
                <h3 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[18px] mb-1">
                  Towns/Cities within {radius.max} miles
                </h3>
                <p className="text-[#908e8e] text-[13px] mb-4">
                  Pick from the dropdown — towns/cities are sourced from
                  Google for your location and radius.
                </p>

                {/* Dropdown — discovered towns the pro hasn't already
                    picked. Selecting one adds it to coverageTowns; the
                    chip then shows below and the option disappears from
                    the list. */}
                {(() => {
                  const unselected = townCatalog.filter(
                    (t) => !coverageTowns.includes(t),
                  );
                  return (
                    <div className="relative">
                      <select
                        value=""
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v) {
                            setCoverageTowns((prev) =>
                              prev.includes(v) ? prev : [...prev, v],
                            );
                            // Reset to the placeholder so the same town
                            // can't appear "selected" in the dropdown.
                            e.target.value = '';
                          }
                        }}
                        disabled={searchingTowns || unselected.length === 0}
                        className="w-full h-[48px] px-4 pr-10 bg-white border border-[#eee] rounded-lg text-[14px] font-bold text-[#3a3335] outline-none focus:border-[#8e2a2f] appearance-none disabled:bg-[#f9f9f9] disabled:cursor-not-allowed"
                      >
                        <option value="" disabled hidden>
                          {searchingTowns
                            ? 'Loading towns from Google…'
                            : townCatalog.length === 0
                              ? 'No towns loaded yet'
                              : unselected.length === 0
                                ? 'All discovered towns added'
                                : `Add a town/city (${unselected.length} available)`}
                        </option>
                        {unselected.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                      <span className="material-icons absolute right-3 top-1/2 -translate-y-1/2 text-[#908e8e] pointer-events-none text-xl">
                        keyboard_arrow_down
                      </span>
                    </div>
                  );
                })()}

                {/* Selected towns appear as removable chips so the pro
                    sees exactly what's covered. */}
                {coverageTowns.length > 0 ? (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {coverageTowns.map((town) => (
                      <span
                        key={town}
                        className="inline-flex items-center gap-1 h-[34px] px-3 bg-[#8e2a2f] text-white rounded-full text-[13px] font-bold"
                      >
                        {town}
                        <button
                          type="button"
                          onClick={() => removeTown(town)}
                          className="ml-1 inline-flex items-center justify-center"
                          aria-label={`Remove ${town}`}
                          title="Remove"
                        >
                          <span className="material-icons text-[14px]">
                            close
                          </span>
                        </button>
                      </span>
                    ))}
                  </div>
                ) : null}

                {/* Free-form fallback — useful when Google's catalog
                    doesn't surface a particular village. */}
                <div className="flex gap-2 mt-5">
                  <input
                    type="text"
                    value={townInput}
                    onChange={(e) => setTownInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addTown();
                      }
                    }}
                    placeholder="Add a town manually"
                    className="flex-1 h-[44px] px-3 bg-white border border-[#eee] rounded-lg text-[14px] outline-none focus:border-[#8e2a2f]"
                  />
                  <button
                    type="button"
                    onClick={addTown}
                    className="h-[44px] px-4 bg-[#8e2a2f] text-white rounded-lg text-[13px] font-bold hover:bg-[#7a2228] transition-colors"
                  >
                    Add
                  </button>
                </div>

                <div className="w-full h-[38px] bg-[#f9f7f2] rounded-[10px] flex items-center justify-center gap-3 px-6 mt-4">
                  <span className="material-icons text-[#8e2a2f] text-[18px]">
                    info_outline
                  </span>
                  <p className="text-[#3a3335] text-[13px] font-bold">
                    Can't find a town you cover? Try expanding your radius.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 8: DBS */}
          {/* Step 8: consolidated documents page — every upload on one
              screen with clear Required / Optional labels. Insurance is
              required for all pros; DBS is required ONLY for childcare
              and an optional "nice to have" for every other category. */}
          {currentSubStep === 8 && (
            <div className="w-full flex flex-col items-stretch gap-5">
              <p className="text-[#908e8e] text-[14px] text-center max-w-[520px] mx-auto leading-relaxed font-bold">
                Upload your documents below. Required documents must be
                provided to finish onboarding — optional ones can be added now
                or later.
              </p>

              {/* One shared hidden picker; the clicked tile decides whether
                  the file lands in the insurance or DBS slot. */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*,application/pdf"
                className="hidden"
              />

              {/* Public liability insurance — required for everyone. */}
              <div className="w-full rounded-[12px] border border-[#eee] bg-white p-5">
                <div className="flex items-center justify-between mb-1 gap-3">
                  <h3 className="font-bold text-[#3a3335] text-[16px]">
                    Public liability insurance
                  </h3>
                  <span className="shrink-0 text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#fdeceb] text-[#8e2a2f]">
                    Required
                  </span>
                </div>
                <p className="text-[13px] text-[#908e8e] mb-4">
                  Upload your public liability insurance certificate.
                </p>

                <div className="w-full flex flex-col items-center mb-4">
                  {insurancePreviewUrl || insuranceFileUrl ? (
                    <FilePreviewTile
                      previewUrl={insurancePreviewUrl}
                      fallbackUrl={insuranceFileUrl}
                      fileName={insuranceFile?.name ?? 'Insurance document'}
                      mimeType={insuranceFile?.type ?? null}
                      onReplace={() => triggerFileUpload('insurance')}
                      onRemove={() => {
                        if (insurancePreviewUrl) {
                          URL.revokeObjectURL(insurancePreviewUrl);
                        }
                        setInsurancePreviewUrl(null);
                        setInsuranceFile(null);
                        setInsuranceFileUrl(null);
                      }}
                      uploading={uploadingFile && !insuranceFileUrl}
                      locked={verifiedLocked}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => triggerFileUpload('insurance')}
                      disabled={uploadingFile}
                      className="w-full max-w-[220px] h-[54px] bg-[#7b8778] hover:bg-[#6a7467] rounded-lg text-white font-bold flex items-center justify-center gap-3 transition-colors mb-2 shadow-sm disabled:opacity-60"
                    >
                      <span className="material-icons">cloud_upload</span>
                      {uploadingFile ? 'Uploading…' : 'Upload Document'}
                    </button>
                  )}
                </div>

                <div className="w-full flex flex-col gap-2">
                  <FloatingInput
                    label="Insurance provider (optional)"
                    value={insuranceProvider}
                    onChange={setInsuranceProvider}
                  />
                  <FloatingInput
                    label="Policy number (optional)"
                    value={insurancePolicy}
                    onChange={setInsurancePolicy}
                  />
                  <div className="mt-2">
                    <p className="text-[12px] text-[#908e8e] font-bold uppercase tracking-wide mb-1">
                      Expiry date <span className="text-[#dc2626]">*</span>
                    </p>
                    <ThemedDatePicker
                      value={insuranceExpiry}
                      onChange={setInsuranceExpiry}
                      invalid={!!insuranceFileUrl && !insuranceExpiry}
                      disabled={verifiedLocked}
                      className="w-full max-w-[260px]"
                    />
                    {verifiedLocked ? (
                      <p className="text-[11px] text-[#065f46] font-bold mt-1">
                        Locked — admin has verified this document.
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* DBS check — mandatory for childcare, optional (encouraged)
                  for every other category. */}
              <div className="w-full rounded-[12px] border border-[#eee] bg-white p-5">
                <div className="flex items-center justify-between mb-1 gap-3">
                  <h3 className="font-bold text-[#3a3335] text-[16px]">
                    DBS check{dbsMandatory ? ' (enhanced)' : ''}
                  </h3>
                  <span
                    className={`shrink-0 text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                      dbsMandatory
                        ? 'bg-[#fdeceb] text-[#8e2a2f]'
                        : 'bg-[#eef3f0] text-[#5f7367]'
                    }`}
                  >
                    {dbsMandatory ? 'Required' : 'Optional'}
                  </span>
                </div>
                <p className="text-[13px] text-[#908e8e] mb-4">
                  {dbsMandatory
                    ? 'Childcare professionals must provide an enhanced DBS check before they can start taking bookings.'
                    : 'Optional — but providing a DBS check gives you access to a wider range of clients.'}
                </p>

                <div className="w-full flex flex-col items-center">
                  {dbsPreviewUrl || dbsFileUrl ? (
                    <FilePreviewTile
                      previewUrl={dbsPreviewUrl}
                      fallbackUrl={dbsFileUrl}
                      fileName={dbsFile?.name ?? 'DBS document'}
                      mimeType={dbsFile?.type ?? null}
                      onReplace={() => triggerFileUpload('dbs')}
                      onRemove={() => {
                        if (dbsPreviewUrl) URL.revokeObjectURL(dbsPreviewUrl);
                        setDbsPreviewUrl(null);
                        setDbsFile(null);
                        setDbsFileUrl(null);
                      }}
                      uploading={uploadingFile && !dbsFileUrl}
                      locked={verifiedLocked}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => triggerFileUpload('dbs')}
                      disabled={uploadingFile}
                      className="w-full max-w-[220px] h-[54px] bg-[#7b8778] hover:bg-[#6a7467] rounded-lg text-white font-bold flex items-center justify-center gap-3 transition-colors mb-2 shadow-sm disabled:opacity-60"
                    >
                      <span className="material-icons">cloud_upload</span>
                      {uploadingFile ? 'Uploading…' : 'Upload Document'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 10: payment — bank details are NOT collected here.
              Pro is handed to Stripe Connect's hosted onboarding from
              /professional/setup-payouts (the wizard's Next click on
              this step navigates the pro to that page; bank account
              number / sort code / IBAN never enter our DOM).
              Compliance reasoning: 2026-06-04 stakeholder review
              flagged any pro-bank PII in our app — even masked / in-
              memory — as out of scope. Stripe holds the real record. */}
          {currentSubStep === 10 && (
            <div className="w-full flex flex-col items-center">
              <p className="text-[#908e8e] text-[14px] text-center max-w-[600px] mb-6 leading-relaxed font-bold">
                Cherry Picked uses Stripe to pay you. You'll add your bank
                details on Stripe's secure page — we never see them.
              </p>

              <div className="w-full bg-[#f9f7f2] rounded-[10px] flex flex-col items-start py-4 px-6 mt-2 mb-8 gap-2">
                <p className="text-[#3a3335] text-[13px] font-bold">
                  What happens next
                </p>
                <ul className="text-[#3a3335] text-[12px] list-disc pl-5 space-y-1">
                  <li>
                    Click Next — we'll create your Stripe account and open
                    Stripe's hosted onboarding page.
                  </li>
                  <li>
                    Verify your identity and add your bank details on Stripe.
                  </li>
                  <li>
                    Stripe brings you back here when you're done. Payouts
                    arrive via UK Faster Payments / Bacs.
                  </li>
                </ul>
              </div>

              <label className="flex items-center gap-3 w-full cursor-pointer group">
                <div className="relative flex items-center justify-center">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={paymentDetails.agreedToTerms}
                    onChange={(e) => {
                      setPaymentDetails({
                        ...paymentDetails,
                        agreedToTerms: e.target.checked,
                      });
                      if (paymentErrors.agreedToTerms)
                        setPaymentErrors((er) => ({
                          ...er,
                          agreedToTerms: undefined,
                        }));
                    }}
                  />
                  <div
                    className={`w-5 h-5 rounded border transition-all flex items-center justify-center ${
                      paymentErrors.agreedToTerms
                        ? 'bg-white border-[#dc2626]'
                        : paymentDetails.agreedToTerms
                          ? 'bg-[#8e2a2f] border-[#8e2a2f]'
                          : 'bg-white border-[#eee] group-hover:border-[#8e2a2f]/50'
                    }`}
                  >
                    {paymentDetails.agreedToTerms && (
                      <span className="material-icons text-white text-[14px]">
                        check
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-[14px] text-[#3a3335]">
                  I agree to the{' '}
                  <button
                    type="button"
                    onClick={(e) => {
                      // Stop the click from toggling the parent
                      // checkbox label — clicking the link should only
                      // open the modal, not flip "agreedToTerms".
                      e.preventDefault();
                      e.stopPropagation();
                      setTermsOpen(true);
                    }}
                    className="text-[#8e2a2f] font-bold underline underline-offset-2 hover:text-[#7a2228] transition-colors"
                  >
                    Terms & Conditions
                  </button>{' '}
                  of Cherry Picked
                </span>
              </label>
              {paymentErrors.agreedToTerms ? (
                <p className="w-full mt-2 px-1 text-[12px] text-[#dc2626] font-['DM_Sans',sans-serif]">
                  {paymentErrors.agreedToTerms}
                </p>
              ) : null}
            </div>
          )}

          <div className="mt-8 w-full max-w-[420px] flex items-center gap-3">
            {currentSubStep > 1 ? (
              <button
                type="button"
                onClick={handleBack}
                disabled={saving || uploadingFile}
                className="px-6 py-4 rounded-[10px] font-['DM_Sans',sans-serif] font-bold text-[#908e8e] text-[14px] bg-[#3a3335]/[0.04] hover:bg-[#3a3335]/[0.08] transition-all disabled:opacity-60"
              >
                Back
              </button>
            ) : null}
            <PrimaryButton
              onClick={handleNext}
              disabled={saving || uploadingFile}
              className="flex-1 py-4"
            >
              {saving
                ? 'Saving…'
                : currentSubStep === 10
                ? 'Finish'
                : 'Next'}
            </PrimaryButton>
          </div>
        </div>
      </main>

      {/* Terms & Conditions overlay — opened from the step-10 checkbox
          link. Pulls content from the CMS so admins can edit it without
          shipping a frontend release. */}
      <CmsModal
        isOpen={termsOpen}
        onClose={() => setTermsOpen(false)}
        code="TERMS_CONDITIONS"
        userType="PROFESSIONAL"
        fallbackTitle="Terms & Conditions"
      />
    </div>
  );
}

// Small preview card shared between the DBS + Insurance steps. Shows
// the picked image as a thumbnail, the picked PDF/file as a generic
// document tile, or a spinner while the S3 upload is in-flight.
//
// `locked` hides Replace/Remove and surfaces an "Approved — locked"
// banner; used once admin has verified the pro so the doc can't be
// silently replaced from the website.
function FilePreviewTile({
  previewUrl,
  fallbackUrl,
  fileName,
  mimeType,
  onReplace,
  onRemove,
  uploading,
  locked = false,
}: {
  previewUrl: string | null;
  fallbackUrl: string | null;
  fileName: string;
  mimeType: string | null;
  onReplace: () => void;
  onRemove: () => void;
  uploading: boolean;
  locked?: boolean;
}) {
  const src = previewUrl ?? fallbackUrl ?? '';
  const isImage = mimeType
    ? mimeType.startsWith('image/')
    : /\.(png|jpe?g|webp|gif)$/i.test(src);
  return (
    <div className="w-full max-w-[280px] rounded-xl border border-[#eee] bg-white p-3 shadow-sm">
      <div className="relative aspect-[4/3] w-full rounded-md overflow-hidden bg-[#f7f7f7] flex items-center justify-center">
        {isImage && src ? (
          <img
            src={src}
            alt={fileName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="flex flex-col items-center justify-center text-[#8e2a2f]">
            <span className="material-icons text-[40px]">description</span>
            <span className="text-[11px] font-bold mt-1 px-2 truncate max-w-full">
              {fileName}
            </span>
          </div>
        )}
        {uploading ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white bg-black/40">
            <span className="material-icons animate-spin text-[24px]">
              progress_activity
            </span>
            <span className="text-[10px] font-bold mt-1 uppercase tracking-wide">
              Uploading…
            </span>
          </div>
        ) : null}
      </div>

      <p className="mt-2 text-[12px] font-bold text-[#3a3335] truncate">
        {fileName}
      </p>
      {!uploading && fallbackUrl ? (
        locked ? (
          <p className="text-[11px] text-[#065f46] font-bold inline-flex items-center gap-1">
            <span className="material-icons text-[12px]">verified</span>
            Approved — locked
          </p>
        ) : (
          <p className="text-[11px] text-[#2e7d32] font-bold">Saved</p>
        )
      ) : null}

      {/* View + Download — prefer the (presigned or blob) preview URL
          because the canonical S3 URL won't load on a private bucket.
          `target="_blank"` lets the browser pick inline rendering for
          PDFs / images; the second link adds `download` so the file
          saves with a sensible filename. */}
      {!uploading && src ? (
        <div className="flex gap-2 mt-3">
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 h-9 rounded-[6px] border border-[#eee] text-[#3a3335] text-[12px] font-bold hover:text-[#8e2a2f] hover:border-[#8e2a2f] transition-colors inline-flex items-center justify-center gap-1"
          >
            <span className="material-icons text-[14px]">visibility</span>
            View
          </a>
          <a
            href={src}
            download={fileName}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 h-9 rounded-[6px] border border-[#eee] text-[#3a3335] text-[12px] font-bold hover:text-[#8e2a2f] hover:border-[#8e2a2f] transition-colors inline-flex items-center justify-center gap-1"
          >
            <span className="material-icons text-[14px]">download</span>
            Download
          </a>
        </div>
      ) : null}

      {locked ? null : (
        <div className="flex gap-2 mt-2">
          <button
            type="button"
            onClick={onReplace}
            disabled={uploading}
            className="flex-1 h-9 rounded-[6px] border border-[#8e2a2f] text-[#8e2a2f] text-[12px] font-bold hover:bg-[#fbeaea] transition-colors disabled:opacity-60"
          >
            Replace
          </button>
          <button
            type="button"
            onClick={onRemove}
            disabled={uploading}
            className="flex-1 h-9 rounded-[6px] border border-[#eee] text-[#908e8e] text-[12px] font-bold hover:text-[#8e2a2f] hover:border-[#8e2a2f] transition-colors disabled:opacity-60"
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
}

